/**
 * Lightweight localStorage persistence for reporting view selections
 * (graph/table view, selected metric, avg/median toggles, comparison, sort, etc.).
 *
 * These are the per-tab "selections" that previously lived only in React state
 * and reset on reload. Column visibility/width/density are persisted separately
 * (useReportingColumnPrefs / useColumnWidths / useTableSettings).
 */

/** Read a persisted view-prefs blob, shallow-merged over defaults. */
export function loadViewPrefs<T extends object>(storageKey: string, defaults: T): T {
    if (!storageKey) return defaults;
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            return { ...defaults, ...parsed };
        }
    } catch {
        // ignore malformed/unavailable storage
    }
    return defaults;
}

/** Persist a view-prefs blob. No-op when storageKey is empty. */
export function saveViewPrefs<T extends object>(storageKey: string, value: T): void {
    if (!storageKey) return;
    try {
        localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
        // ignore quota/unavailable storage
    }
}
